import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, X, Loader2, Play, Pause, MessageCircle, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { computeZodiac, numerology } from "../lib/astrology";
import { useLang } from "../context/LanguageContext";
import ConsultationPaywall from "./ConsultationPaywall";
import { CONSULTATION_FEE_DISPLAY } from "../lib/consultationConfig";
import MouthCanvas from "./MouthCanvas";
import TalkingHeadVideo from "./TalkingHeadVideo";

/**
 * Floating "Ask the Gemologist" assistant.
 *
 * - ONE master avatar (generated server-side via Nano Banana, cached forever).
 * - Script generated per-session via Claude (Emergent LLM key).
 * - Voice via browser-native Web Speech API (free, zero credits).
 * - Mouth animation: canvas overlay reacts to SpeechSynthesisUtterance `onboundary`
 *   events + a sine-wave envelope while speaking. No video re-render.
 * - Idle: avatar visible, mouth closed, subtle ambient breath via CSS transform.
 *
 * Props:
 *   - gemstoneId?: string  — when present, narration includes the product
 *   - placement?: "floating" | "inline"  — default "floating"
 */
const GemologistAssistant = ({ gemstoneId = null, placement = "floating" }) => {
  const { apiLang } = useLang();
  const [open, setOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarVideoUrl, setAvatarVideoUrl] = useState("");
  const [avatarErr, setAvatarErr] = useState(false);
  const [loadingScript, setLoadingScript] = useState(false);
  const [script, setScript] = useState("");
  const [profile, setProfile] = useState(null);
  const [voiceLang, setVoiceLang] = useState("en-US");
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [speaking, setSpeaking] = useState(false);

  // Local instant zodiac preview while we wait for the Claude script
  const localZodiac = useMemo(() => (birthDate ? computeZodiac(birthDate) : null), [birthDate]);
  const localNumerology = useMemo(
    () => (name || birthDate ? numerology(name, birthDate) : null),
    [name, birthDate]
  );

  // Lazy-fetch the master avatar the FIRST time the modal opens (re-fetches
  // when the active locale changes so a culturally distinct Sora video is served).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/gemologist/avatar", { params: { lang: apiLang || "en" } });
        if (cancelled) return;
        if (data?.data_url) setAvatarUrl(data.data_url);
        else setAvatarErr(true);
        if (data?.video_url) {
          // Prepend the API origin so absolute URL works in dev/preview
          const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
          setAvatarVideoUrl(`${base}${data.video_url}`);
        } else {
          setAvatarVideoUrl("");
        }
      } catch {
        if (!cancelled) setAvatarErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, apiLang]);

  // Stop speech when the modal closes
  useEffect(() => {
    if (!open && typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, [open]);

  const askGemologist = async () => {
    setLoadingScript(true);
    try {
      const { data } = await api.post("/gemologist/script", {
        gemstone_id: gemstoneId || undefined,
        name: name || undefined,
        birth_date: birthDate || undefined,
        lang: apiLang || "en",
      });
      setScript(data.script || "");
      setProfile(data.profile || null);
      setVoiceLang(data.voice_lang || "en-US");
      // Auto-play right away
      setTimeout(() => speak(data.script, data.voice_lang || "en-US"), 50);
    } finally {
      setLoadingScript(false);
    }
  };

  const speak = (text, lang) => {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang || "en-US";
    u.rate = 0.95;
    u.pitch = 1.0;
    u.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang === lang)
      || voices.find((v) => v.lang?.startsWith((lang || "en").slice(0, 2)));
    if (match) u.voice = match;
    u.onstart = () => { setSpeaking(true); window.__opikSpeechWord = 0; };
    u.onend = () => { setSpeaking(false); window.__opikSpeechWord = -1; };
    u.onerror = () => { setSpeaking(false); window.__opikSpeechWord = -1; };
    // Track word boundaries → MouthAnimator reads `window.__opikSpeechWord` for each boundary tick
    u.onboundary = (e) => {
      if (e.name === "word") {
        const idx = (window.__opikSpeechWord ?? 0) + 1;
        window.__opikSpeechWord = idx;
        // Capture the just-spoken word so we can pick a viseme shape
        const upcoming = (text.slice(e.charIndex, e.charIndex + (e.charLength || 1)) || "").toLowerCase();
        window.__opikSpeechViseme = pickViseme(upcoming);
      }
    };
    window.speechSynthesis.speak(u);
  };

  const stopSpeak = () => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  };

  const isInline = placement === "inline";

  return (
    <>
      {!isInline && (
        <button
          data-testid="gemologist-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 bg-gold text-black font-accent text-[11px] tracking-[0.22em] uppercase px-5 py-3.5 shadow-[0_8px_30px_rgba(212,175,55,0.35)] hover:bg-ivory hover:scale-105 transition-all duration-500"
          aria-label="Ask the Gemologist"
        >
          <Sparkles size={14} />
          Ask the Gemologist
        </button>
      )}

      {isInline && (
        <button
          data-testid="gemologist-inline-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2.5 border border-gold text-gold font-accent text-[11px] tracking-[0.24em] uppercase px-6 py-4 hover:bg-gold hover:text-black transition-colors duration-500"
        >
          <Sparkles size={14} /> Ask the Gemologist
        </button>
      )}

      {open && (
        <div
          data-testid="gemologist-modal"
          className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm overflow-y-auto"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-w-4xl mx-auto my-8 md:my-12 bg-noir border border-gold/40 shadow-[0_0_80px_rgba(212,175,55,0.18)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-white/10">
              <div>
                <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-1">Maison Concierge</div>
                <h2 className="font-heading text-3xl text-ivory">The Gemologist</h2>
              </div>
              <button
                data-testid="gemologist-close"
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white p-2"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* AVATAR */}
              <div className="bg-onyx p-6 md:p-8 border-b md:border-b-0 md:border-r border-white/10">
                <GemologistAvatar
                  avatarUrl={avatarUrl}
                  videoUrl={avatarVideoUrl}
                  speaking={speaking}
                  loading={!avatarUrl && !avatarErr}
                  error={avatarErr}
                />
                {script && (
                  <div className="mt-5 flex items-center gap-3">
                    {speaking ? (
                      <button
                        data-testid="gemologist-pause"
                        onClick={stopSpeak}
                        className="flex items-center gap-2 border border-gold text-gold font-accent text-[10px] tracking-[0.24em] uppercase px-4 py-2.5 hover:bg-gold hover:text-black transition-colors"
                      >
                        <Pause size={12} /> Stop
                      </button>
                    ) : (
                      <button
                        data-testid="gemologist-replay"
                        onClick={() => speak(script, voiceLang)}
                        className="flex items-center gap-2 border border-gold text-gold font-accent text-[10px] tracking-[0.24em] uppercase px-4 py-2.5 hover:bg-gold hover:text-black transition-colors"
                      >
                        <Play size={12} /> Replay
                      </button>
                    )}
                    <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40">{voiceLang}</span>
                  </div>
                )}
              </div>

              {/* INPUTS + RESULT */}
              <div className="p-6 md:p-8">
                {!script ? (
                  <>
                    <p className="text-white/60 text-sm mb-6 leading-relaxed">
                      Share a few details and I'll reveal which gemstone resonates with your astrology, numerology and birthstone.
                    </p>
                    <label className="block mb-4">
                      <span className="text-white/50 text-[10px] tracking-[0.24em] font-accent uppercase block mb-2">Your Name</span>
                      <input
                        data-testid="gemologist-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Aria Wijaya"
                        className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-gold"
                      />
                    </label>
                    <label className="block mb-6">
                      <span className="text-white/50 text-[10px] tracking-[0.24em] font-accent uppercase block mb-2">Birth Date (Optional)</span>
                      <input
                        data-testid="gemologist-dob"
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        max={new Date().toISOString().slice(0, 10)}
                        className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-gold"
                      />
                    </label>

                    {/* Live local astrology preview */}
                    {localZodiac && (
                      <div data-testid="gemologist-preview" className="border border-gold/30 bg-black/40 p-4 mb-6">
                        <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-gold mb-2">Provisional Reading</div>
                        <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                          <div><span className="text-white/40 text-xs">Zodiac</span><div className="font-heading text-lg">{localZodiac.name}</div></div>
                          <div><span className="text-white/40 text-xs">Element</span><div className="font-heading text-lg">{localZodiac.element}</div></div>
                          <div><span className="text-white/40 text-xs">Birthstone</span><div className="font-heading text-lg">{localZodiac.birthstone}</div></div>
                          <div><span className="text-white/40 text-xs">Lucky Stone</span><div className="font-heading text-lg">{localZodiac.lucky}</div></div>
                          {localNumerology?.life_path && (
                            <div><span className="text-white/40 text-xs">Life Path</span><div className="font-heading text-lg">{localNumerology.life_path}</div></div>
                          )}
                          {localNumerology?.destiny && (
                            <div><span className="text-white/40 text-xs">Destiny</span><div className="font-heading text-lg">{localNumerology.destiny}</div></div>
                          )}
                        </div>
                      </div>
                    )}

                    <button
                      data-testid="gemologist-submit"
                      onClick={askGemologist}
                      disabled={loadingScript}
                      className="w-full flex items-center justify-center gap-2 bg-gold text-black font-accent text-[11px] tracking-[0.28em] uppercase py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50"
                    >
                      {loadingScript ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                      {loadingScript ? "Composing…" : "Begin Reading"}
                    </button>
                    <p className="text-white/30 text-[10px] tracking-[0.18em] uppercase font-accent mt-4 text-center">
                      Voice plays through your device speakers
                    </p>

                    {/* Upsell teaser — paid Professional Consultation */}
                    <button
                      type="button"
                      data-testid="gemologist-upsell-teaser"
                      onClick={() => setPaywallOpen(true)}
                      className="mt-6 w-full border border-gold/30 hover:border-gold bg-gold/5 hover:bg-gold/10 text-white/70 hover:text-white px-4 py-3 text-left flex items-center justify-between gap-3 transition-colors"
                    >
                      <span>
                        <span className="block font-accent text-[10px] tracking-[0.22em] uppercase text-gold mb-0.5">
                          Professional Consultation · {CONSULTATION_FEE_DISPLAY}
                        </span>
                        <span className="block text-xs text-white/60">Get a thorough, paid one-on-one reading</span>
                      </span>
                      <ShieldCheck size={16} className="text-gold flex-shrink-0" />
                    </button>
                  </>
                ) : (
                  <>
                    {profile && (
                      <div data-testid="gemologist-profile" className="border border-gold/30 bg-black/40 p-4 mb-5">
                        <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-gold mb-2">Your Reading</div>
                        <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                          <div><span className="text-white/40 text-xs">Zodiac</span><div className="font-heading text-lg">{profile.zodiac_name}</div></div>
                          <div><span className="text-white/40 text-xs">Element</span><div className="font-heading text-lg">{profile.element}</div></div>
                          <div><span className="text-white/40 text-xs">Birthstone</span><div className="font-heading text-lg">{profile.birthstone_name}</div></div>
                          <div><span className="text-white/40 text-xs">Lucky Stone</span><div className="font-heading text-lg">{profile.lucky_stone}</div></div>
                          {profile.life_path && <div><span className="text-white/40 text-xs">Life Path</span><div className="font-heading text-lg">{profile.life_path}</div></div>}
                          {profile.destiny && <div><span className="text-white/40 text-xs">Destiny</span><div className="font-heading text-lg">{profile.destiny}</div></div>}
                        </div>
                      </div>
                    )}
                    <div data-testid="gemologist-script-text" className="text-white/80 text-base leading-relaxed font-light italic border-l-2 border-gold/40 pl-4">
                      "{script}"
                    </div>
                    <button
                      data-testid="gemologist-reset"
                      onClick={() => { setScript(""); setProfile(null); stopSpeak(); }}
                      className="mt-6 text-white/50 hover:text-gold font-accent text-[10px] tracking-[0.24em] uppercase"
                    >
                      Ask Another Question
                    </button>

                    {/* PRIMARY UPSELL — paid Professional Consultation */}
                    <button
                      type="button"
                      data-testid="gemologist-upsell-cta"
                      onClick={() => setPaywallOpen(true)}
                      className="mt-8 w-full flex items-center justify-between gap-4 border border-gold bg-gold/10 hover:bg-gold hover:text-black text-white px-5 py-4 transition-colors duration-500 group"
                    >
                      <span className="flex items-center gap-3">
                        <ShieldCheck size={16} className="text-gold group-hover:text-black flex-shrink-0" />
                        <span>
                          <span className="block font-accent text-[10px] tracking-[0.24em] uppercase text-gold group-hover:text-black mb-0.5">
                            Professional Gemologist Consultation
                          </span>
                          <span className="block text-sm">One detailed paid reading · {CONSULTATION_FEE_DISPLAY}</span>
                        </span>
                      </span>
                      <span className="font-accent text-[10px] tracking-[0.24em] uppercase">Begin →</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {paywallOpen && (
        <ConsultationPaywall
          onClose={() => setPaywallOpen(false)}
          gemstoneId={gemstoneId}
        />
      )}
    </>
  );
};

// ============================================================
//  GemologistAvatar — viseme-driven mouth animation sync'd to SpeechSynthesis
// ============================================================

/**
 * Map a spoken word to one of 5 viseme shapes (mouth poses).
 * Far more believable than a sine wave: real human speech alternates
 * between closed (m/b/p), wide (ee), open (ah), round (oh/oo), and mid-open.
 *
 * Returns: {open: 0-1 vertical scale, wide: 0-1 horizontal scale}
 */
export const pickViseme = (word) => {
  const w = (word || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return { open: 0.0, wide: 0.5 };
  // Find first vowel cluster, fall back to first consonant
  const m = w.match(/(oo|ou|ow|oa|o|aw|au|ah|a|ai|ay|ee|ea|i|e|u)/);
  if (!m) {
    // Mostly consonants — closed/labial
    if (/^[mbp]/.test(w)) return { open: 0.05, wide: 0.5 };
    return { open: 0.35, wide: 0.55 };
  }
  const v = m[1];
  if (/^o(o|u|w|a)?$/.test(v) || v === "aw" || v === "au") return { open: 0.7, wide: 0.25 }; // round "o/oo/aw"
  if (v === "ee" || v === "ea" || v === "i") return { open: 0.35, wide: 0.95 };               // wide "ee/i"
  if (v === "ah" || v === "a" || v === "ai" || v === "ay") return { open: 0.95, wide: 0.6 };  // open "ah"
  if (v === "e" || v === "u") return { open: 0.55, wide: 0.55 };                              // mid "e/u"
  return { open: 0.5, wide: 0.55 };
};

const GemologistAvatar = ({ avatarUrl, videoUrl, speaking, loading, error }) => {
  return (
    <div className="relative w-full aspect-[4/5] bg-black overflow-hidden border border-white/10" data-testid="gemologist-avatar">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-white/40">
          <Loader2 size={24} className="animate-spin" />
        </div>
      )}
      {error && !avatarUrl && (
        <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs px-4 text-center">
          Avatar unavailable — please retry shortly.
        </div>
      )}
      {avatarUrl && (
        <>
          {/* Still avatar — hidden when video is available so we don't show two different faces */}
          {!videoUrl && (
            <img
              src={avatarUrl}
              alt="Senior Gemologist of Opik Jewels"
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                filter: "saturate(1.05) contrast(1.05)",
                animation: speaking ? "none" : "gemologist-breath 5s ease-in-out infinite",
              }}
            />
          )}
          {/* PREFERRED: Sora-2 talking-head loop — covers entire frame, plays on speak, pauses when idle */}
          <TalkingHeadVideo videoUrl={videoUrl} speaking={speaking} />
          {/* FALLBACK: canvas viseme overlay when no video is generated yet */}
          {!videoUrl && <MouthCanvas speaking={speaking} />}
          {speaking && (
            <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-gold/40 px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
              <span className="font-accent text-[9px] tracking-[0.24em] uppercase text-gold">Speaking</span>
            </div>
          )}
        </>
      )}
      <style>{`
        @keyframes gemologist-breath {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.008); }
        }
      `}</style>
    </div>
  );
};

export default GemologistAssistant;
